<template>
  <v-container fluid class="pa-0">
    <EntityIndex
      :loading="loading"
      :total-count="totalCount"
      :table-headers="tableHeaders"
      :table-rows="tableRows"
      :table-options.sync="tableOptions"
      :selected-rows.sync="selectedRows"
      :selected-rows-actions="selectedRowsActions"
      :no-data-text="$t('serviceRequest.noServiceRequests')"
      item-key="serviceRequest.id">
      <template v-slot:actions>
        <v-btn
          v-if="$featurePermissions.serviceRequestCreate"
          color="primary"
          elevation="2"
          class="mb-2"
          @click="$refs.serviceRequestDialog.openCreateNewServiceRequest()">
          <v-icon>mdi-plus</v-icon>
          {{ $t('actions.add') }}
        </v-btn>
      </template>

      <template v-slot:item.message="{ item }">
        <router-link :to="item.routerLinkTo">
          <div class="message-cell-text">
            {{ item.serviceRequest.message }}
          </div></router-link>
      </template>

      <template v-slot:entityListItem="props">
        <service-request-list-item
          :item="props.item"
          :show-actions="true"
          :row-click-enabled="true"
        />
      </template>
    </EntityIndex>

    <entity-confirm-delete-dialog
      ref="confirmDeleteDialog"
      :text="$t('serviceRequest.deleteConfirm')"
      :text-plural="$t('serviceRequest.deleteConfirmPlural')"
      @confirmedDelete="deleteServiceRequests">
      <template v-slot:entityListItem="props">
        <service-request-list-item :item="props.item" />
      </template>
    </entity-confirm-delete-dialog>

    <service-request-dialog
      ref="serviceRequestDialog"
      @done="fetch"
    ></service-request-dialog>
  </v-container>
</template>

<style scoped>
.message-cell-text {
  white-space: nowrap;
  overflow: hidden;
  display: block;
  text-overflow: ellipsis;
  max-width: 20em;
}
</style>

<script>
import i18n from '@/i18n';
import EntityIndex from '@/components/entity-crud/EntityIndex';
import EntityConfirmDeleteDialog from '@/components/entity-crud/EntityDeleteConfirmDialog';
import ServiceRequestDialog from '@/views/servicerequest/ServiceRequestDialog';
import ServiceRequestListItem from '@/views/servicerequest/ServiceRequestListItem';
import ServiceRequestsService from '@/services/ServiceRequestsService';

export default {
  components: {
    ServiceRequestListItem,
    ServiceRequestDialog,
    EntityConfirmDeleteDialog,
    EntityIndex,
  },
  name: 'ServiceRequestIndex',
  data() {
    return {
      serviceRequests: [],
      selectedRows: [],
      loading: false,
      totalCount: 0,
    };
  },
  computed: {
    tableOptions: {
      get() {
        return this.$store.state.serviceRequestIndexState.tableOptions;
      },
      set(value) {
        this.$store.commit('serviceRequestIndexState/setTableOptions', value);
      },
    },

    tableHeaders() {
      return [
        {
          value: 'location',
          text: i18n.t('location.locationTop'),
          getValue: (serviceRequest) => {
            return this.$options.filters.location(serviceRequest.location);
          },
        },
        {
          value: 'device',
          text: i18n.t('device.singular'),
          getValue: (serviceRequest) => {
            return this.$options.filters.device(serviceRequest.device);
          },
        },
        {
          value: 'message',
          text: i18n.t('serviceRequest.message'),
          getValue: (serviceRequest) => {
            return serviceRequest.message;
          },
        },

        {
          value: 'availabilityWindow',
          text: i18n.t('serviceRequest.availabilityWindow'),
          getValue: (serviceRequest) => {
            let start = this.$options.filters.ellipsisMissing(
              this.$options.filters.date(serviceRequest.serviceWindowStart),
            );
            let end = this.$options.filters.ellipsisMissing(
              this.$options.filters.date(serviceRequest.serviceWindowEnd),
            );
            return start + ' - ' + end;
          },
        },

        {
          value: 'reportingUser',
          text: i18n.t('serviceRequest.reportingUser'),
          getValue: (serviceRequest) => {
            return this.$options.filters.user(serviceRequest.reportingUser);
          },
        },
        {
          value: 'inviter',
          text: i18n.t('serviceRequest.inviter'),
          getValue: (serviceRequest) => {
            return serviceRequest.inviter;
          },
        },
        {
          value: 'created',
          text: i18n.t('serviceRequest.created'),
          getValue: (serviceRequest) => {
            return this.$options.filters.dateTime(serviceRequest.created);
          },
        },
        {
          value: 'state',
          text: i18n.t('serviceRequest.state'),
          getValue: (serviceRequest) => {
            return this.$options.filters.serviceRequestState(
              serviceRequest.state,
            );
          },
        },
        {
          value: 'rowActions',
          text: '',
          getValue: (serviceRequest) => {
            return this.createRowActions(serviceRequest);
          },
        },
      ];
    },
    tableRows() {
      return this.serviceRequests.map((serviceRequest) => {
        let row = {};
        row.serviceRequest = serviceRequest;
        row.routerLinkTo = `servicerequests/${serviceRequest.id}`;

        row.rowClick = () => {
          this.$router.push(row.routerLinkTo);
        };

        this.tableHeaders.forEach((header) => {
          if (header.getValue) {
            row[header.value] = header.getValue(serviceRequest);
          }
        });
        return row;
      });
    },
    selectedRowsActions() {
      const actions = [
        {
          label: i18n.t('actions.delete'),
          icon: 'mdi-delete',
          disabled: this.selectedRows.length === 0,
          hasPermission: this.$featurePermissions.serviceRequestCRUD,
          click: () => {
            this.confirmServiceRequestDelete(this.selectedRows);
          },
        },
      ];
      return actions.filter((action) => action.hasPermission);
    },
  },
  methods: {
    createRowActions(serviceRequest) {
      let actions = [
        {
          label: i18n.t('actions.show'),
          icon: 'mdi-view-list',
          hasPermission: true,
          click: () => {
            this.$router.push(`servicerequests/${serviceRequest.id}`);
          },
        },
        {
          label: i18n.t('actions.modify'),
          icon: 'mdi-pencil-outline',
          hasPermission: this.$featurePermissions.serviceRequestCRUD,
          click: () => {
            this.openEditDialog(serviceRequest.id);
          },
        },
        {
          label: i18n.t('actions.delete'),
          icon: 'mdi-delete',
          dividerBefore: true,
          hasPermission: this.$featurePermissions.serviceRequestCRUD,
          click: () => {
            this.confirmServiceRequestDelete([
              { serviceRequest: serviceRequest },
            ]);
          },
        },
      ];

      return actions.filter((action) => action.hasPermission);
    },

    confirmServiceRequestDelete(items) {
      this.$refs.confirmDeleteDialog.show(items);
    },
    deleteServiceRequests(items) {
      const serviceRequestIds = items.map((item) => item.serviceRequest.id);
      ServiceRequestsService.delete(serviceRequestIds).finally(() => {
        this.$refs.confirmDeleteDialog.close();
        this.fetch();
      });
    },

    openEditDialog(serviceRequestId) {
      this.$refs.serviceRequestDialog.openEditForServiceRequest(
        serviceRequestId,
      );
    },

    fetch() {
      this.loading = true;
      this.selectedRows = [];
      ServiceRequestsService.list({
        limit: this.tableOptions.itemsPerPage,
        offset: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
        orderBy: this.tableOptions.sortBy[0],
        order: this.tableOptions.sortDesc[0] ? 'DESC' : 'ASC',
      })
        .then((result) => {
          this.loading = false;
          this.serviceRequests = result.data.items;
          this.totalCount = result.data.totalCount;
        })
        .catch((result) => {
          console.log(JSON.stringify(result));
          this.loading = false;
        });
    },
  },

  watch: {
    tableOptions: {
      handler() {
        this.fetch();
      },
      deep: true,
    },
  },
  created() {
    this.fetch();
  },
};
</script>
